/**
 * 订单管理
 */
import type { PageContext } from '@zto/zpage'
import { logDialog, refundDialog } from './_dialogs'
import { UI_PAGE_EVENTS } from '@zto/zpage-ui-element'
import { commonOptions } from '@/config/options'
import {
  OrderStatus,
  OrderStatusName,
  HasRefundName,
  PayStatusName,
  RefundStatusName,
  RefundStatus,
  OrderChannelName,
  OrderChannel
} from '@/config/enums'
import dayjs from 'dayjs'
import { ElLoading } from 'element-plus'

export default {
  body: {
    type: 'crud',
    actions: {
      query: {
        api: '/cabinet/admin/oms/AdminOmsOrderQueryController/page'
      },
      detail: {
        label: '详情',
        link: {
          title: '订单详情（${data.row.orderCode}）',
          name: 'tuda.cabinet.order.detail',
          path: '/cabinet/order/detail',
          query: {
            orderCode: '${data.row.orderCode}'
          }
        }
      },
      refund: {
        label: '退款',
        api: '/cabinet/admin/oms/AdminOmsOrderCommandController/applyRefund',
        ...refundDialog()
        //
        // payload: {
        //   orderCode: '${data.row.orderCode}'
        // },
        // message: '确定要标记该订单为已退款吗?'
      },
      log: {
        label: '日志',
        dialog: logDialog
      },
      customExport: {
        label: '导出全部',
        customAction: true, // 重要：确保 onAction 被执行
        message: false, // 重要：不显示确认对话框，直接执行
        payload: async (ctx: PageContext, ext: any) => {
          const searchParams = ext?.searchParams
          return {
            ...searchParams
          }
        },
        onAction: async (ctx: PageContext, actionCfg: any) => {
          const { app } = ctx
          const { Message } = app.useMessage()

          // 显示全屏loading
          const loadingInstance = ElLoading.service({
            lock: true,
            text: '正在导出数据，请稍候...',
            background: 'rgba(0, 0, 0, 0.7)'
          })

          try {
            const searchParams: any = ctx.data || {}

            const size = 1000 // 每页大小
            let current = 1 // 从第1页开始
            let lastId = null
            let allData: any[] = [] // 存储所有数据
            let hasMoreData = true // 是否还有更多数据

            // 循环查询直到数据为空或报错
            while (hasMoreData) {
              try {
                const res: any = await app.request({
                  url: '/cabinet/admin/oms/AdminOmsOrderQueryController/queryForExport',
                  data: {
                    ...searchParams,
                    current: current,
                    size: size,
                    lastId: lastId
                  }
                })

                const pageData: any[] = res.list || []
                lastId = res.lastId || null

                // 如果当前页没有数据，说明已经查询完所有数据
                if (!pageData || pageData.length === 0) {
                  hasMoreData = false
                  break
                }

                // 将当前页数据添加到总数据中
                allData = allData.concat(pageData)

                if (pageData.length < size) {
                  hasMoreData = false
                  break
                }

                // 继续查询下一页
                current++

                // 更新提示信息
                // Message.info(`已查询 ${allData.length} 条数据，继续查询第 ${current} 页...`)
              } catch (pageError: any) {
                // 如果查询出错，停止查询
                console.error(`查询第 ${current} 页时出错:`, pageError)
                hasMoreData = false

                // 如果已经有数据，继续导出；如果没有数据，提示错误
                if (allData.length === 0) {
                  throw pageError
                }
                break
              }
            }

            // 4. 检查是否有数据可导出
            if (!allData || allData.length === 0) {
              Message.warning('没有可导出的数据')
              return
            }

            // Message.info(`共查询到 ${allData.length} 条数据，正在准备导出...`)

            // 5. 获取列配置
            const columns = [
              { label: '订单编号', prop: 'orderCode' },
              {
                label: '订单状态',
                prop: 'orderStatus',
                formatter: (row: any) => {
                  return (OrderStatusName as any)[row.orderStatus] || row.orderStatus
                }
              },
              {
                label: '订单渠道',
                prop: 'orderChannel',
                formatter: (row: any) => {
                  return (OrderChannelName as any)[row.orderChannel] || row.orderChannel
                }
              },
              { label: '运单号', prop: 'waybillNo' },
              { label: '用户手机号', prop: 'mobile' },
              { label: '商品名称', prop: 'productName' },
              { label: '商品编号', prop: 'productCode' },
              { label: '价格', prop: 'buyAmount' },
              { label: '门店名称', prop: 'shopName' },
              { label: '门店编号', prop: 'shopCode' },
              {
                label: '退款状态',
                prop: 'refundOrderStatus',
                formatter: (row: any) => {
                  return (RefundStatusName as any)[row.refundOrderStatus] || row.refundOrderStatus
                }
              },
              { label: '柜机编号', prop: 'inCabinetCode' },
              { label: '柜机序号', prop: 'inCabinetSerialNumber' },
              { label: '格口信息', prop: 'inCabinetCell' },
              {
                label: '创建时间',
                prop: 'gmtCreate',
                formatter: (row: any) => {
                  return row.gmtCreate ? dayjs(row.gmtCreate).format('YYYY-MM-DD HH:mm:ss') : '--'
                }
              },
              { label: '支付方式', prop: 'payMethod' },
              {
                label: '支付状态',
                prop: 'payStatus',
                formatter: (row: any) => {
                  return (PayStatusName as any)[row.payStatus] || row.payStatus
                }
              },
              { label: '支付流水号', prop: 'paymentFlowCode' },
              {
                label: '支付时间',
                prop: 'payTime',
                formatter: (row: any) => {
                  return row.payTime ? dayjs(row.payTime).format('YYYY-MM-DD HH:mm:ss') : '--'
                }
              }
            ]

            // 6. 格式化数据（应用 formatter）
            const formattedData = allData.map((row: any) => {
              const formattedRow: any = {}
              columns.forEach((col: any) => {
                if (col.formatter) {
                  formattedRow[col.prop] = col.formatter(row)
                } else {
                  formattedRow[col.prop] = row[col.prop] || '--'
                }
              })
              return formattedRow
            })

            const exportDataFn = app.useComponentsConfig('xlsx.exportData')

            if (exportDataFn) {
              exportDataFn(formattedData, {
                columns: columns,
                fileName: '订单列表',
                app
              })
            }
            Message.success(`导出成功，共导出 ${formattedData.length} 条数据`)
          } catch (error: any) {
            console.error('导出失败:', error)
            // Message.error(error?.message || '导出失败，请稍后重试')
          } finally {
            // 关闭全屏loading
            loadingInstance.close()
          }
        }
      }
    },
    searchOn: [UI_PAGE_EVENTS.ACTIVATED],
    search: {
      items: [
        { type: 'input', prop: 'orderCode', label: '订单编号', span: 6 },
        { type: 'input', prop: 'mobile', label: '用户手机号', span: 6 },
        { type: 'input', prop: 'productName', label: '商品名称', span: 6 },
        { type: 'input', prop: 'waybillNo', label: '运单号', span: 6 },
        // { type: 'input', prop: 'shopName', label: '门店名称', span: 6 },
        {
          type: 'fuzzy-select',
          prop: 'shopCode',
          labelProp: 'shopName',
          label: '门店名称',
          optionValueProp: 'shopCode',
          optionLabelProp: 'shopName',
          triggerFocus: true,
          remote: true,
          keywordProp: 'shopName',
          api: '/cabinet/admin/base/AdminBaseShopQueryController/queryList',
          span: 6
        },
        {
          label: '退款状态',
          type: 'select',
          prop: 'refundOrderStatus',
          options: commonOptions.refundStatusOptions,
          span: 6
        },
        {
          label: '状态',
          type: 'select',
          prop: 'orderStatuses',
          multiple: true,
          options: commonOptions.orderStatus,
          span: 6
        },
        {
          label: '创建时间',
          type: 'date-range-picker',
          prop: 'orderStartTime',
          toProp: 'orderEndTime',
          defaultTo: 'now',
          defaultRange: 90,
          maxRange: 365
        }
      ]
    },
    toolbar: {
      noExport: true,
      items: [{ action: 'customExport' }]
    },
    table: {
      operation: {
        width: 150,
        items: [
          { action: 'detail' },
          // 已完成 - 标记退款
          {
            action: 'refund',
            visibleOn: `this.data.row.orderChannel === '${OrderChannel.TUDA}' && this.data.row.orderStatus === '${OrderStatus.COMPLETED}' && ['${RefundStatus.WAIT_REFUND}','${RefundStatus.REFUND_FAIL}'].includes(this.data.row.refundOrderStatus)`
          },
          { action: 'log' }
        ]
      },
      columns: [
        { label: '订单编号', prop: 'orderCode', minWidth: 180 },
        {
          label: '订单状态',
          minWidth: 100,
          formatter: (row: any) => {
            return (OrderStatusName as any)[row.orderStatus] || row.orderStatus
          }
        },
        {
          label: '订单渠道',
          minWidth: 100,
          formatter: (row: any) => {
            return (OrderChannelName as any)[row.orderChannel] || row.orderChannel
          }
        },
        { label: '运单号', prop: 'waybillNo', minWidth: 180 },
        { label: '用户手机号', prop: 'mobile', minWidth: 120 },
        { label: '商品编号', prop: 'productCode', minWidth: 180 },
        { label: '商品名称', prop: 'productName', minWidth: 120 },
        { label: '价格', prop: 'buyAmount', minWidth: 120 },
        { label: '门店名称', prop: 'shopName', minWidth: 120 },
        { label: '门店编号', prop: 'shopCode', minWidth: 120 },
        {
          label: '退款状态',
          minWidth: 100,
          formatter: (row: any) => {
            return (RefundStatusName as any)[row.refundOrderStatus] || row.refundOrderStatus
          }
        },
        { label: '柜机编号', prop: 'cabinetCode', minWidth: 120 },
        { label: '柜机序号', prop: 'inCabinetSerialNumber', minWidth: 120 },
        { label: '格口信息', prop: 'cabinetCell', minWidth: 120 },
        {
          label: '创建时间',
          minWidth: 150,
          formatter: (row: any) => {
            return row.gmtCreate ? dayjs(row.gmtCreate).format('YYYY-MM-DD HH:mm:ss') : '--'
          }
        }
      ]
    }
  }
}
